import type { InferGetStaticPropsType } from 'next';
import Head from 'next/head';
import type { ReactElement } from 'react';

import BlogsList from '@/components/Blog/BlogsList';
import Layout from '@/components/Landing/Layout';
import { getStaticBlogsListProps } from '@/utils/getStaticBlog';
import { createBreadcrumbSchema } from '@/utils/schema-org';
import { createMetaTags } from '@/utils/seo';

const TITLE = 'Manufacturing Blog - AI for Construction Product Manufacturers';
const DESCRIPTION =
  'AI for construction product manufacturers - case studies, implementation guides, and industry trends from the MindPal team.';

const ManufacturingBlog = ({
  initialPages,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  return (
    <>
      <Head>
        {createMetaTags({
          title: `${TITLE} - MindPal`,
          description: DESCRIPTION,
        })}
        <link
          rel="canonical"
          href="https://mindpal.co/manufacturing-blog"
          key="canonical"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'CollectionPage',
              name: 'Manufacturing Blog — MindPal',
              description: DESCRIPTION,
              url: 'https://mindpal.co/manufacturing-blog',
              publisher: {
                '@type': 'Organization',
                name: 'MindPal',
                url: 'https://mindpal.co',
              },
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              createBreadcrumbSchema('/manufacturing-blog')
            ),
          }}
        />
      </Head>
      <BlogsList
        initialPages={initialPages}
        blogType="CONTECH"
        title="Manufacturing Blog"
        description="Field-tested AI for construction product manufacturers"
      />
    </>
  );
};

export default ManufacturingBlog;

ManufacturingBlog.getLayout = (page: ReactElement) => (
  <Layout theme="light">{page}</Layout>
);

export const getStaticProps = async () => {
  const { initialPages } = await getStaticBlogsListProps({
    blogType: 'CONTECH',
  });

  return { props: { initialPages }, revalidate: 30 };
};
